import type { MessageDescriptor } from "@lingui/core";

import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { RefreshCcw } from "lucide-react";
import { observer } from "mobx-react-lite";
import {
  Fragment,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type {
  DefaultPatientEventResponse,
  PlotData,
  TimeSeriesTrace,
} from "../../../api";

import {
  afibDisclaimerMsg,
  afibResearchUseMsg,
} from "../../../app/common-messages";
import { Button } from "../../../components/ui-kit/button";
import {
  DataZoomRange,
  EChartsChart,
  ItemAction,
} from "../../../components/ui-kit/echarts-chart";
import { resolveEnumLabel } from "../../../components/ui-kit/fields/utils";
import { getServerNow } from "../../../utils/server-time";
import {
  eventCategory,
  eventCategoryDetails,
  eventCategoryType,
} from "../events/enums";
import {
  ChartSection,
  MIN_CHART_WIDTH,
  StatTrace,
  SUMMARY_GUTTER,
} from "./chart-section";
import {
  afibProbabilityLines,
  buildAfibOption,
  buildBloodPressureOption,
  buildHeartcoreOption,
  buildEventsOption,
  eventsBarHeight,
  buildHeartRateOption,
  buildRollingMetricOption,
  buildSpo2Option,
  buildSymptomsOption,
  buildWeightOption,
  buildZoomBarOption,
  CHART_COLORS,
  ChartEvent,
  computeChartTimeRange,
  DRI_ROLLING,
  EventShape,
  findTraceByFacets,
  hasAfibData,
  heartRateLines,
  itemPpgUuids,
  LVFP_ROLLING,
  REF_LINES_SERIES,
  rollingMetricTraces,
  spo2ConditionPalette,
  spo2TraceKey,
  spo2TraceName,
  symptomDividerOffsets,
  symptomsChartHeight,
} from "./heartcore-chart-options";
import {
  calendarDateToLocalNoon,
  DAY_MS,
  formatChartDate,
} from "./heartcore-format";
import {
  DEFAULT_HEARTCORE_RANGE,
  HeartcoreRange,
  HeartcoreVariant,
  useHeartcoreChartData,
} from "./use-heartcore-chart-data";

// observer: `events` is a MobX observable array. eventsList memoizes on its
// identity, which works because Patient.fetchEvents replaces it wholesale.
export const HeartCoreCharts = observer(function HeartCoreCharts(props: {
  patientId: number;
  variant: HeartcoreVariant;
  events?: DefaultPatientEventResponse[];
  /** Caller holds the AFIB permission bit — gates the AFib probability chart. */
  canAfib?: boolean;
  /** Caller can open the PPG table — makes the raw DRI points link to it. */
  canOpenPpgs?: boolean;
  /** Bump (after clearHeartcoreReports) to force a refetch — "Reload data". */
  reloadNonce?: number;
  /** How far back to show — the default window or the patient's full history.
   *  Governs both the report fetched and the events kept (see eventsList). */
  range?: HeartcoreRange;
  /** Rendered on the left of the control row (opposite the zoom presets) —
   *  the tab's QuickLook button and, on HeartCore, the range selects. */
  leading?: ReactNode;
  /**
   * The charts live inside their own bounded scroll container rather than
   * scrolling with the page (the Daily Monitoring Trends sheet). The zoom bar
   * then pins with CSS `sticky` against that container, so none of the
   * page-level fold measurement applies — see useStickyBottomBar.
   */
  scopedScroll?: boolean;
  /** A row of buttons above the charts that scroll to each one (the Trends window). */
  jumpBar?: boolean;
  /** Rendered at the end of the jump bar — the Trends window's export button. */
  jumpBarEnd?: ReactNode;
  /** Laid out for the PDF export — see ChartSection's prop of the same name. */
  printing?: boolean;
}) {
  const { t } = useLingui();
  const { variant } = props;

  // Interaction state lifted here instead of `echarts.connect()`, which would
  // also broadcast tooltips across charts.
  const [axisTimeMs, setAxisTimeMs] = useState<number | null>(null);
  // Drives every chart EXCEPT the zoom bar — the bar is the source of this via
  // drags, so feeding it back would fight the drag (jitter).
  const [dataZoomRange, setDataZoomRange] = useState<DataZoomRange | null>(
    null,
  );
  // The zoom bar's input: updated by presets/reset but never by the bar's own
  // drag, so the bar is never re-driven mid-drag.
  const [zoomBarCommand, setZoomBarCommand] = useState<DataZoomRange | null>(
    null,
  );
  // Applied preset (days) for button highlighting; cleared on manual drag.
  const [activePreset, setActivePreset] = useState<number | null>(null);

  const {
    driLvfpPlot,
    hrPlot,
    afibPlot,
    bpPlot,
    symptomsPlot,
    weightPlot,
    spo2Plot,
    metricLabel,
    loading,
    error,
    retry,
  } = useHeartcoreChartData(
    props.patientId,
    variant,
    props.reloadNonce,
    props.range,
  );

  const isDark = useDarkModeSignal();

  // From the patient events endpoint — the report JSON carries no events, and
  // that endpoint takes no date range, so it always returns the patient's whole
  // history. Trimming it to the report's own window here is not cosmetic: event
  // dates are folded into the shared time domain below, so a patient with three
  // years of events would stretch every chart across three years and squeeze
  // six months of measurements into a sliver at the right-hand edge.
  const eventsList = useMemo(() => {
    const cutoffMs = eventWindowStartMs(props.range ?? DEFAULT_HEARTCORE_RANGE);
    if (cutoffMs === null) return (props.events ?? []).map(toChartEvent);
    return (props.events ?? []).map(toChartEvent).filter((e) => {
      const ms = new Date(e.date).getTime();
      // Fail open. `NaN >= cutoff` is false, so comparing directly would drop
      // every event whose date we couldn't read — the whole bar disappearing,
      // with nothing on screen to say why.
      return Number.isNaN(ms) || ms >= cutoffMs;
    });
  }, [props.events, props.range]);

  // Event dates are folded into the shared domain so an event newer than the
  // last measurement stays reachable instead of being clipped off every chart.
  const timeRange = useMemo(
    () =>
      computeChartTimeRange(
        [
          driLvfpPlot,
          hrPlot,
          afibPlot,
          bpPlot,
          symptomsPlot,
          weightPlot,
          spo2Plot,
        ],
        eventsList.map((e) => e.date),
      ),
    [
      driLvfpPlot,
      hrPlot,
      afibPlot,
      bpPlot,
      symptomsPlot,
      weightPlot,
      spo2Plot,
      eventsList,
    ],
  );
  const zoomBarOption = useThemedMemo(
    () => (timeRange ? buildZoomBarOption(timeRange.min, timeRange.max) : null),
    [timeRange],
    isDark,
  );

  const handleZoomChange = (range: DataZoomRange) => {
    setDataZoomRange(range);
    setActivePreset(null);
  };

  // A reload unmounts the zoom bar; on remount it would restore a stale
  // zoomBarCommand (drags update only dataZoomRange) while the charts keep the
  // live range. Seed the command when a load starts so both agree. Ref, not a
  // dep, so this never fires mid-drag.
  const dataZoomRangeRef = useRef(dataZoomRange);
  dataZoomRangeRef.current = dataZoomRange;
  useEffect(() => {
    if (loading) setZoomBarCommand(dataZoomRangeRef.current);
  }, [loading]);

  // Changing the range ("Load all data" and back) swaps in a domain of a
  // completely different width. The zoom is held as a percentage of that
  // domain, so it survives the swap and silently comes to mean something else:
  // clicking "Load all data" while zoomed would load the full history and then
  // show you an arbitrary slice of it, which reads as the button not working.
  // Reset to the whole span — the point of the click is to see all of it.
  //
  // Ref-guarded so this only fires on an actual change: keyed on props.range
  // alone it would also run on mount and on every sub-tab remount, dispatching
  // a redundant zoom at every one of them.
  const previousRangeRef = useRef(props.range);
  useEffect(() => {
    if (previousRangeRef.current === props.range) return;
    previousRangeRef.current = props.range;
    const full = { start: 0, end: 100 };
    setDataZoomRange(full);
    setZoomBarCommand(full);
    setActivePreset(null);
  }, [props.range]);

  const resetZoom = () => {
    const range = { start: 0, end: 100 };
    setDataZoomRange(range);
    setZoomBarCommand(range);
    setActivePreset(null);
  };

  // A timestamp as a percentage of the shared domain — the unit the zoom bar
  // and every chart's dataZoom speak. Clamped, so a drag that ran off into the
  // gutter (where convertFromPixel extrapolates past the domain) still lands.
  const toPct = (ms: number) => {
    if (!timeRange) return 0;
    const minMs = new Date(timeRange.min).getTime();
    const span = new Date(timeRange.max).getTime() - minMs;
    if (span <= 0) return 0;
    return Math.max(0, Math.min(100, ((ms - minMs) / span) * 100));
  };

  // Zoom to the trailing `days`, anchored at the right edge. Percentages map
  // onto the zoom bar's domain, driving the bar and every linked chart.
  const applyZoomPreset = (days: number) => {
    if (!timeRange) return;
    const maxMs = new Date(timeRange.max).getTime();
    const range = { start: toPct(maxMs - days * DAY_MS), end: 100 };
    setDataZoomRange(range);
    setZoomBarCommand(range);
    setActivePreset(days);
  };

  // A band drag-selected on any chart. Unlike the zoom bar's own drag this must
  // also command the bar — the gesture started somewhere else, so the bar's
  // handles have to follow rather than being left behind.
  const handleZoomSelect = (startMs: number, endMs: number) => {
    if (!timeRange) return;
    const range = { start: toPct(startMs), end: toPct(endMs) };
    setDataZoomRange(range);
    setZoomBarCommand(range);
    setActivePreset(null);
  };

  const sticky = useStickyBottomBar(!!props.scopedScroll);

  // AFib sits under Heart Rate on every tab — it's a property of the patient,
  // not of the metric the tab is about — gated only on the AFIB bit. The BE
  // already filters the section out without the permission; the explicit check
  // also covers the `afib_probability` fallback shape, which hangs off the
  // Heart rate plot and so isn't filtered along with it.
  const showAfib = !!props.canAfib && hasAfibData(afibPlot, hrPlot);
  // One row for the tab's leading controls (QuickLook button, range selects)
  // and, pushed right, the zoom presets. Rendered in every state below so the
  // leading controls never disappear. Presets are disabled while loading: the
  // report is nulled mid-fetch, so the domain is events-only and a preset
  // would compute the wrong window.
  const controlRow =
    props.leading || zoomBarOption ? (
      <div className="mb-3 flex flex-wrap items-center gap-3 print:hidden">
        {props.leading}
        {zoomBarOption && (
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {ZOOM_PRESETS.map((preset) => (
              <Button
                key={preset.days}
                size="sm"
                // Which preset is applied: the pressed state fills the button,
                // and carries it to a screen reader and to a test as well.
                aria-pressed={activePreset === preset.days}
                disabled={loading}
                onClick={() => applyZoomPreset(preset.days)}
              >
                {i18n._(preset.label)}
              </Button>
            ))}
            {/* Reset to the full range — the discoverable equivalent of
                double-clicking the zoom bar. */}
            <Button
              variant="secondary"
              size="sm"
              icon={RefreshCcw}
              disabled={loading}
              onClick={resetZoom}
              label={t`Reset zoom to full range`}
            />
          </div>
        )}
      </div>
    ) : null;

  if (loading) {
    return (
      <div className="mb-5">
        {controlRow}
        <div className="text-muted-foreground animate-pulse py-8 text-center">
          <Trans>Loading charts…</Trans>
        </div>
      </div>
    );
  }

  // Explicit error, not "no data" (indistinguishable from a patient without
  // measurements); rendered even when events exist so an events-only chart
  // can't masquerade as a successful load.
  if (error) {
    return (
      <div className="mb-5">
        {controlRow}
        <div className="py-8 text-center">
          <p className="text-muted-foreground mb-3">
            <Trans>Failed to load chart data.</Trans>
          </p>
          <Button size="sm" onClick={retry}>
            <Trans>Try again</Trans>
          </Button>
        </div>
      </div>
    );
  }

  // The HeartCore tab always shows its primary chart — when the patient has
  // fewer than 4 measurements it renders empty with a "not enough data" note
  // (see buildHeartcoreOption), so it also counts as content here.
  const showHeartcorePrimary =
    variant === "heartcore" || plotHasData(driLvfpPlot);
  const hasAnyData =
    showHeartcorePrimary ||
    plotHasData(hrPlot) ||
    showAfib ||
    plotHasData(bpPlot) ||
    plotHasData(symptomsPlot) ||
    plotHasData(weightPlot) ||
    plotHasData(spo2Plot) ||
    eventsList.length > 0;

  if (!hasAnyData) {
    return (
      <div className="mb-5">
        {controlRow}
        <div className="text-muted-foreground py-8 text-center">
          <Trans>No chart data available.</Trans>
        </div>
      </div>
    );
  }

  // Every section stacks its panel on top once the plot would fall under
  // MIN_CHART_WIDTH — and always on paper, where the plot gets the full width.
  // The events and zoom bars must drop the panel gutter in step, or their time
  // axes stop lining up with the charts'.
  const stacked =
    !!props.printing ||
    (sticky.containerWidth > 0 &&
      sticky.containerWidth - SUMMARY_GUTTER < MIN_CHART_WIDTH);
  const gutter = stacked ? 0 : SUMMARY_GUTTER;

  // No ruler in print, so nothing for the CURRENT row or events bar to follow.
  const rulerMs = props.printing ? null : axisTimeMs;
  const shared: ChartShared = {
    axisTimeMs: rulerMs,
    onAxisTimeChange: setAxisTimeMs,
    dataZoomRange,
    onZoomSelect: handleZoomSelect,
    onZoomReset: resetZoom,
    isDark,
    stacked,
    printing: !!props.printing,
    // Clear of the sticky events bar when a section is jumped to. Only the
    // jump bar scrolls to sections, and it lives in the scoped (header-less)
    // container, so no app header offset.
    scrollMarginTop:
      eventsList.length > 0
        ? eventsBarHeight(eventsList) + EVENTS_BAR_MARGIN_BOTTOM
        : 0,
  };
  // The secondary charts in render order, each only when it has something to
  // show. One list for the charts and the jump bar, so the two can't drift.
  const sections = (
    [
      // One Heart Rate chart on every tab: the algorithm × condition lines all
      // live in it, with only the default one shown (see heartRateLines).
      [
        "hr",
        plotHasData(hrPlot) && (
          <HeartRateChart plot={hrPlot} timeRange={timeRange} shared={shared} />
        ),
      ],
      [
        "afib",
        showAfib && (
          <AfibChart
            plot={afibPlot}
            hrPlot={hrPlot}
            timeRange={timeRange}
            shared={shared}
          />
        ),
      ],
      [
        "bp",
        plotHasData(bpPlot) && (
          <BloodPressureChart
            plot={bpPlot}
            timeRange={timeRange}
            shared={shared}
          />
        ),
      ],
      [
        "weight",
        plotHasData(weightPlot) && (
          <WeightChart
            plot={weightPlot}
            timeRange={timeRange}
            shared={shared}
          />
        ),
      ],
      [
        "symptoms",
        plotHasData(symptomsPlot) && (
          <SymptomsChart
            plot={symptomsPlot}
            timeRange={timeRange}
            shared={shared}
          />
        ),
      ],
      [
        "spo2",
        plotHasData(spo2Plot) && (
          <Spo2Chart plot={spo2Plot} timeRange={timeRange} shared={shared} />
        ),
      ],
    ] satisfies [ChartAnchor, ReactNode][]
  ).filter(([, chart]) => chart);

  return (
    <div
      ref={sticky.setContainerEl}
      className="relative mb-5"
      style={{
        // Only the out-of-flow bar needs space reserved for it; the scoped bar
        // is a normal flow child and takes its own.
        paddingBottom:
          zoomBarOption && !props.scopedScroll ? ZOOM_BAR_HEIGHT : 0,
      }}
    >
      {props.jumpBar && (
        <div className="mb-4 flex flex-wrap justify-center gap-2 print:hidden">
          {sections.map(([anchor]) => (
            <Button
              key={anchor}
              size="sm"
              onClick={() =>
                document
                  .querySelector(`[data-chart="${anchor}"]`)
                  ?.scrollIntoView({ behavior: "smooth" })
              }
            >
              {i18n._(CHART_TITLES[anchor])}
            </Button>
          ))}
          {props.jumpBarEnd}
        </div>
      )}
      {controlRow}
      {props.printing && (
        <p className="mb-3 text-center text-sm font-medium">
          {printedRange(timeRange, dataZoomRange)}
        </p>
      )}
      <div className="relative">
        {eventsList.length > 0 && (
          <EventsBar
            events={eventsList}
            timeRange={timeRange}
            dataZoomRange={dataZoomRange}
            axisTimeMs={rulerMs}
            isDark={isDark}
            gutter={gutter}
            stickyTop={props.scopedScroll ? 0 : HEADER_OFFSET}
          />
        )}
        {showHeartcorePrimary && (
          <DriLvfpChart
            plot={driLvfpPlot}
            metricLabel={metricLabel}
            timeRange={timeRange}
            variant={variant}
            shared={shared}
            patientId={props.patientId}
            canOpenPpgs={props.canOpenPpgs}
          />
        )}
        {zoomBarOption && (
          <div ref={sticky.setTopSentinelEl} style={{ height: 1 }} />
        )}

        {sections.map(([anchor, chart]) => (
          <Fragment key={anchor}>{chart}</Fragment>
        ))}

        {/* Bottom sentinel at the natural zoom bar position; IntersectionObserver needs non-zero area */}
        {zoomBarOption && (
          <div ref={sticky.setBottomSentinelEl} style={{ height: 1 }} />
        )}
      </div>

      {zoomBarOption && (
        <div
          className="bg-background border-t border-border print:hidden"
          onDoubleClick={resetZoom}
          title={t`Double-click to reset zoom`}
          style={
            // Every mode stops short of the summary-panel gutter so the bar's
            // time axis stays aligned with the charts above it.
            props.scopedScroll
              ? {
                  // Inside a scroll container, `sticky` is what `fixed` + fold
                  // measurement emulates for the page: the bar rides the
                  // container's bottom edge while the charts scroll past it,
                  // with no JS and nothing escaping the panel.
                  // A margin, not `right`: on a sticky element that's only an
                  // offset and leaves the bar spanning the gutter.
                  position: "sticky",
                  bottom: 0,
                  marginRight: gutter,
                  height: ZOOM_BAR_HEIGHT,
                  zIndex: 10,
                }
              : sticky.zoomMode === "fixed"
                ? {
                    position: "fixed",
                    bottom: 0,
                    left: sticky.containerLeft,
                    width: Math.max(sticky.containerWidth - gutter, 0),
                    height: ZOOM_BAR_HEIGHT,
                    // Page chrome, level with the app header (z-40): above
                    // the charts, below every overlay (dialogs, popovers and
                    // the mobile nav sheet all sit at z-50).
                    zIndex: 40,
                  }
                : {
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: gutter,
                    height: ZOOM_BAR_HEIGHT,
                  }
          }
        >
          <EChartsChart
            option={zoomBarOption}
            dataZoomRange={zoomBarCommand}
            onDataZoomChange={handleZoomChange}
            style={{ height: "100%" }}
          />
        </div>
      )}
    </div>
  );
});

// ---------------------------------------------------------------------------
// Per-chart subcomponents — each owns its option/trace memos so the parent
// isn't a wall of hooks. Memos go through useThemedMemo (see its comment).
// ---------------------------------------------------------------------------

function EventsBar(props: {
  events: ChartEvent[];
  timeRange: TimeRange;
  dataZoomRange: DataZoomRange | null;
  axisTimeMs: number | null;
  isDark: boolean;
  /** Right inset matching the charts' summary panels (0 when they stack). */
  gutter: number;
  /**
   * What the bar pins against, as a CSS length (a number is px). On the page
   * that's the app header it sits below; inside a bounded scroll container
   * (`scopedScroll`) there is no such header, and any offset would leave the
   * bar hanging that far down the panel, over the first chart instead of
   * above it.
   */
  stickyTop: number | string;
}) {
  const { events, timeRange, dataZoomRange, axisTimeMs, isDark } = props;
  const option = useThemedMemo(
    () => buildEventsOption(events, timeRange),
    [events, timeRange],
    isDark,
  );
  const height = eventsBarHeight(events);
  return (
    <div
      className="bg-background border-b border-border print:static"
      style={{
        position: "sticky",
        top: props.stickyTop,
        height,
        marginBottom: EVENTS_BAR_MARGIN_BOTTOM,
        // Same gutter the ChartSections give their summary panels, so this
        // bar's time axis lines up with theirs.
        paddingRight: props.gutter,
        zIndex: 30,
      }}
    >
      <EChartsChart
        option={option}
        dataZoomRange={dataZoomRange}
        axisPointerValue={axisTimeMs}
        persistGraphicRuler
        evenTimeTicks
        rulerColor={CHART_COLORS.rulerInk}
        style={{ height: "100%" }}
      />
    </div>
  );
}

function DriLvfpChart(props: {
  plot: PlotData | null;
  metricLabel: string;
  timeRange: TimeRange;
  variant: HeartcoreVariant;
  shared: ChartShared;
  patientId: number;
  canOpenPpgs?: boolean;
}) {
  const { plot, metricLabel, timeRange, variant, shared } = props;
  const { patientId, canOpenPpgs } = props;
  // Which rolling config (if any) this variant renders; both memos branch on it.
  const rolling =
    variant === "dri" ? DRI_ROLLING : variant === "lvfp" ? LVFP_ROLLING : null;

  const option = useThemedMemo(
    () =>
      rolling
        ? buildRollingMetricOption(plot, metricLabel, timeRange, rolling)
        : buildHeartcoreOption(plot, metricLabel, timeRange),
    [plot, metricLabel, timeRange, rolling],
    shared.isDark,
  );

  const traces: StatTrace[] = useThemedMemo(
    () => {
      if (rolling) {
        const rawLabel = rolling.rawTransform;
        const rollingLabel = rolling.rollingTransform;
        const { raw, rolling: rollingTrace } = rollingMetricTraces(
          plot,
          rolling,
        );
        // Both rows list the shared thresholds series, so (OR-combined in
        // ChartSection) the limits stay until BOTH lines are hidden. The
        // rolling row also toggles its separate markers series.
        const thresholdsName = `${rollingLabel} thresholds`;
        return [
          {
            label: rawLabel,
            color: CHART_COLORS.neutralMuted,
            ...statLine(raw),
            seriesName: rawLabel,
            seriesNames: [rawLabel, thresholdsName],
            shape: "circle",
          },
          {
            label: rollingLabel,
            color: CHART_COLORS.arrowInk,
            ...statLine(rollingTrace),
            seriesName: rollingLabel,
            seriesNames: [
              rollingLabel,
              `${rollingLabel} markers`,
              thresholdsName,
              // The continuous-run triangles annotate this line, so they come
              // and go with it rather than being selectable in their own right.
              rolling.trendRun.label,
            ],
            shape: "arrow",
          },
        ];
      }
      return [
        {
          label: metricLabel,
          color: CHART_COLORS.gridInk, // HeartCore single line
          ...statLine(plotTraces(plot)[0]),
        },
      ];
    },
    [plot, metricLabel, rolling],
    shared.isDark,
  );

  // A raw DRI point opens the PPG table, filtered to the PPGs it was computed
  // from, in a new tab — as the codebase report's DRI plot does. Points the
  // report carries no uuids for stay plain.
  const itemAction = useMemo((): ItemAction | undefined => {
    if (variant !== "dri" || !canOpenPpgs) return undefined;
    return (item) => {
      if (item.seriesName !== DRI_ROLLING.rawTransform) return null;
      const uuids = itemPpgUuids(item.data);
      if (uuids.length === 0) return null;
      const query = new URLSearchParams(uuids.map((uuid) => ["uuid", uuid]));
      return () =>
        window.open(`/patient/${patientId}/raw/ppg?${query}`, "_blank");
    };
  }, [variant, canOpenPpgs, patientId]);

  return (
    <ChartSection
      {...shared}
      title={metricLabel}
      option={option}
      traces={traces}
      itemAction={itemAction}
      lineStats={plot?.line_stats}
      // LVFP (its own tab and the HeartCore headline) is a pressure, read to a
      // tenth of a mmHg; DRI is a unitless 0–100 score, where a decimal is noise.
      unit={variant === "dri" ? undefined : "mmHg"}
      decimals={variant === "dri" ? 0 : 1}
      lastValueThresholdGt={plot?.last_value_threshold_gt}
      deltaColorRule={plot?.delta_color_rule}
    />
  );
}

function HeartRateChart(props: {
  plot: PlotData | null;
  timeRange: TimeRange;
  shared: ChartShared;
}) {
  const { plot, timeRange, shared } = props;
  const option = useThemedMemo(
    () => buildHeartRateOption(plot, timeRange),
    [plot, timeRange],
    shared.isDark,
  );
  // Themed: the QPPG source's `color` getter resolves arrowInk at memo time.
  // Every row also lists the shared reference-lines series, so (OR-combined in
  // ChartSection) the bpm limits stay until every line is hidden. heartRateLines
  // also decides which single line starts visible, and the option builder above
  // reads the same call — see its comment.
  const traces: StatTrace[] = useThemedMemo(
    () =>
      heartRateLines(plot).map((line) => ({
        label: line.label,
        color: line.color,
        points: line.points,
        facets: line.facets,
        key: line.key,
        seriesName: line.label,
        seriesNames: [line.label, REF_LINES_SERIES],
        visible: line.visible,
      })),
    [plot],
    shared.isDark,
  );
  return (
    <ChartSection
      {...shared}
      title={i18n._(CHART_TITLES.hr)}
      anchor="hr"
      lastValueThresholdGt={plot?.last_value_threshold_gt}
      deltaColorRule={plot?.delta_color_rule}
      option={option}
      traces={traces}
      lineStats={plot?.line_stats}
      unit="bpm"
    />
  );
}

function AfibChart(props: {
  plot: PlotData | null;
  /** Fallback source: older payloads carry `afib_probability` on the HR plot. */
  hrPlot: PlotData | null;
  timeRange: TimeRange;
  shared: ChartShared;
}) {
  const { plot, hrPlot, timeRange, shared } = props;
  const option = useThemedMemo(
    () => buildAfibOption(plot, hrPlot, timeRange),
    [plot, hrPlot, timeRange],
    shared.isDark,
  );
  // Themed: the grey line shades flip with the theme. Each row toggles its line
  // together with the banded-dot overlay drawn on top of it — they're one
  // logical series split in two only so the visualMap can repaint the dots
  // without touching the line. afibProbabilityLines also decides which single
  // condition starts visible, and buildAfibOption reads the same call.
  const traces: StatTrace[] = useThemedMemo(
    () =>
      afibProbabilityLines(plot, hrPlot).map((line) => ({
        label: line.label,
        color: line.color,
        points: line.points,
        key: line.key,
        seriesName: line.label,
        seriesNames: [line.label, line.dotsSeries],
        visible: line.visible,
      })),
    [plot, hrPlot],
    shared.isDark,
  );
  return (
    <ChartSection
      {...shared}
      title={i18n._(CHART_TITLES.afib)}
      anchor="afib"
      titleHint={i18n._(afibDisclaimerMsg)}
      footnote={i18n._(afibResearchUseMsg)}
      option={option}
      traces={traces}
      unit="%"
      // No lastValueThresholdGt or deltaColorRule: the report has no AFib
      // stats, so its LAST VALUE and % badges stay neutral even if either
      // ever rides on the plot.
    />
  );
}

function BloodPressureChart(props: {
  plot: PlotData | null;
  timeRange: TimeRange;
  shared: ChartShared;
}) {
  const { plot, timeRange, shared } = props;
  const option = useThemedMemo(
    () => buildBloodPressureOption(plot, timeRange),
    [plot, timeRange],
    shared.isDark,
  );
  const traces: StatTrace[] = useThemedMemo(
    () => {
      const list = plotTraces(plot);
      return [
        {
          label: "Systolic",
          color: CHART_COLORS.brandPrimary,
          ...statLine(
            findTraceByFacets(list, {
              "Measurement Type": "Blood Pressure Systolic",
            }),
          ),
          seriesName: "Systolic",
        },
        {
          label: "Diastolic",
          color: CHART_COLORS.brandSecondary,
          ...statLine(
            findTraceByFacets(list, {
              "Measurement Type": "Blood Pressure Diastolic",
            }),
          ),
          seriesName: "Diastolic",
        },
      ];
    },
    [plot],
    shared.isDark,
  );
  return (
    <ChartSection
      {...shared}
      title={i18n._(CHART_TITLES.bp)}
      anchor="bp"
      lastValueThresholdGt={plot?.last_value_threshold_gt}
      deltaColorRule={plot?.delta_color_rule}
      option={option}
      traces={traces}
      lineStats={plot?.line_stats}
      unit="mmHg"
    />
  );
}

function SymptomsChart(props: {
  plot: PlotData | null;
  timeRange: TimeRange;
  shared: ChartShared;
}) {
  const { plot, timeRange, shared } = props;
  const option = useThemedMemo(
    () => buildSymptomsOption(plot, timeRange),
    [plot, timeRange],
    shared.isDark,
  );
  const traces: StatTrace[] = useThemedMemo(
    () => {
      const list = plotTraces(plot);
      return [
        {
          label: "Shortness of breath",
          color: CHART_COLORS.brandPrimary,
          ...statLine(
            findTraceByFacets(list, {
              "Symptom Name": "Shortness Of Breath",
            }),
          ),
          seriesName: "Shortness of breath",
        },
        {
          label: "Fatigue score",
          color: CHART_COLORS.brandSecondary,
          ...statLine(
            findTraceByFacets(list, {
              "Symptom Name": "Fatigue Score",
            }),
          ),
          seriesName: "Fatigue score",
        },
        {
          label: "Perceived health change",
          color: CHART_COLORS.arrowInk,
          ...statLine(
            findTraceByFacets(list, {
              "Symptom Name": "Perceived Health Change",
            }),
          ),
          seriesName: "Perceived health change",
        },
        // Drop symptoms with no data so the stat rows match the visible sub-grids.
      ].filter((t) => t.points.length > 0);
    },
    [plot],
    shared.isDark,
  );
  return (
    <ChartSection
      {...shared}
      title={i18n._(CHART_TITLES.symptoms)}
      anchor="symptoms"
      lastValueThresholdGt={plot?.last_value_threshold_gt}
      deltaColorRule={plot?.delta_color_rule}
      option={option}
      traces={traces}
      lineStats={plot?.line_stats}
      chartHeight={symptomsChartHeight(plot)}
      subDividers={symptomDividerOffsets(plot)}
    />
  );
}

function WeightChart(props: {
  plot: PlotData | null;
  timeRange: TimeRange;
  shared: ChartShared;
}) {
  const { plot, timeRange, shared } = props;
  const option = useThemedMemo(
    () => buildWeightOption(plot, timeRange),
    [plot, timeRange],
    shared.isDark,
  );
  const traces: StatTrace[] = useThemedMemo(
    () => [
      {
        label: "Weight",
        color: CHART_COLORS.brandPrimary,
        ...statLine(plotTraces(plot)[0]),
      },
    ],
    [plot],
    shared.isDark,
  );
  return (
    <ChartSection
      {...shared}
      title={i18n._(CHART_TITLES.weight)}
      anchor="weight"
      lastValueThresholdGt={plot?.last_value_threshold_gt}
      deltaColorRule={plot?.delta_color_rule}
      option={option}
      traces={traces}
      lineStats={plot?.line_stats}
      unit="kg"
    />
  );
}

function Spo2Chart(props: {
  plot: PlotData | null;
  timeRange: TimeRange;
  shared: ChartShared;
}) {
  const { plot, timeRange, shared } = props;
  const option = useThemedMemo(
    () => buildSpo2Option(plot, timeRange),
    [plot, timeRange],
    shared.isDark,
  );
  // Only the leading condition starts visible, as on the HR conditions chart.
  const traces: StatTrace[] = useThemedMemo(
    () => {
      const palette = spo2ConditionPalette();
      return plotTraces(plot).map((t, i) => {
        const name = spo2TraceName(t);
        return {
          label: name,
          color: palette[i % palette.length],
          points: t.points,
          facets: t.facets,
          key: spo2TraceKey(t),
          seriesName: name,
          visible: i === 0,
        };
      });
    },
    [plot],
    shared.isDark,
  );
  return (
    <ChartSection
      {...shared}
      title={i18n._(CHART_TITLES.spo2)}
      anchor="spo2"
      lastValueThresholdGt={plot?.last_value_threshold_gt}
      deltaColorRule={plot?.delta_color_rule}
      option={option}
      traces={traces}
      lineStats={plot?.line_stats}
      unit="%"
    />
  );
}

// ---------------------------------------------------------------------------
// Shared constants, types, hooks and helpers
// ---------------------------------------------------------------------------

// Must fit buildZoomBarOption's grid/dataZoom geometry (slider bottom 12 +
// height 28 → 40px minimum) with a little breathing room.
const ZOOM_BAR_HEIGHT = 56;
const EVENTS_BAR_MARGIN_BOTTOM = 16;
// Sticky offset below the app header; each layout sets the variable to its
// header's height (main-layout.tsx).
const HEADER_OFFSET = "var(--app-header-height)";

// Trailing-N-days zoom presets, anchored at the latest data point.
const ZOOM_PRESETS: { label: MessageDescriptor; days: number }[] = [
  { label: msg`Last week`, days: 7 },
  { label: msg`Last month`, days: 30 },
  { label: msg`Last quarter`, days: 90 },
];

// Interaction state shared by all chart subcomponents. `isDark` isn't read
// directly — it feeds the useThemedMemo deps so options rebuild on theme flips.
//
// Spread wholesale into ChartSection (`{...shared}`), so everything here except
// `isDark` is one of its props by the same name — keep the two in step when
// adding a field. Spread first, never last: a JSX spread isn't excess-checked,
// so a field added here under an existing prop's name would otherwise override
// it silently rather than failing to compile.
type ChartShared = {
  axisTimeMs: number | null;
  onAxisTimeChange: (v: number | null) => void;
  dataZoomRange: DataZoomRange | null;
  onZoomSelect: (startMs: number, endMs: number) => void;
  onZoomReset: () => void;
  isDark: boolean;
  /** Narrow container — see the `stacked` prop on ChartSection. */
  stacked: boolean;
  scrollMarginTop: number;
  printing: boolean;
};

/** The secondary charts, by the `data-chart` anchor their sections carry. */
type ChartAnchor = "hr" | "afib" | "bp" | "weight" | "symptoms" | "spo2";

/** Section titles, shared by each chart and its jump-bar button. */
const CHART_TITLES: Record<ChartAnchor, MessageDescriptor> = {
  hr: msg`Heart Rate`,
  afib: msg`AFib probability`,
  bp: msg`Blood Pressure`,
  weight: msg`Weight`,
  symptoms: msg`Symptoms`,
  spo2: msg`SpO₂`,
};

type TimeRange = { min: string; max: string } | null;

function plotHasData(plot: PlotData | null): boolean {
  return (plot?.timeseries?.traces ?? []).some((t) => t.points.length > 0);
}

// A stat row's points, and the facets that pick its entry in `line_stats`.
function statLine(trace: TimeSeriesTrace | undefined) {
  return { points: trace?.points ?? [], facets: trace?.facets };
}

function plotTraces(plot: PlotData | null) {
  return plot?.timeseries?.traces ?? [];
}

// useMemo with the builders' invisible deps appended: they read theme-aware
// CHART_COLORS getters and localized condition names (i18n._ via
// ppgConditionText), neither of which eslint can see through. One helper keeps
// both overrides in a single place — and keeps every chart's option memo and
// stat-row memo on the *same* deps, so their series names can't drift apart.
function useThemedMemo<T>(build: () => T, deps: unknown[], isDark: boolean): T {
  const { i18n } = useLingui();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(build, [...deps, isDark, i18n.locale]);
}

// Watches `<html>` for the `.dark` class toggle so chart options rebuild with
// the theme-aware CHART_COLORS inks. Callers feed the result into useThemedMemo.
function useDarkModeSignal(): boolean {
  const [isDark, setIsDark] = useState(
    () =>
      typeof document !== "undefined" &&
      document.documentElement.classList.contains("dark"),
  );
  useEffect(() => {
    const html = document.documentElement;
    const observer = new MutationObserver(() => {
      setIsDark(html.classList.contains("dark"));
    });
    observer.observe(html, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);
  return isDark;
}

// Keeps the zoom bar `position: fixed` at the viewport bottom while scrolling
// the charts, switching to "natural" once the bottom sentinel comes into view.
// Also lifts the chat bubble via --chat-bubble-offset.
//
// Sentinels are state (callback refs), not plain refs: the component mounts
// through early returns that don't render them, so effects keyed on data can
// settle before the nodes exist and never attach their observers (zoom bar
// stuck "natural" after a tab switch). Keying on node appearance fixes that.
/**
 * Drives the zoom bar's page-level sticky behaviour. `scoped` turns all of it
 * off: inside a bounded scroll container the bar uses CSS `sticky` against that
 * container instead, so measuring the page fold would only fight it — and the
 * body offset below is a page concern that a panel must not write.
 */
function useStickyBottomBar(scoped = false) {
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
  const [topSentinelEl, setTopSentinelEl] = useState<HTMLDivElement | null>(
    null,
  );
  const [bottomSentinelEl, setBottomSentinelEl] =
    useState<HTMLDivElement | null>(null);
  const [zoomMode, setZoomMode] = useState<"fixed" | "natural">("natural");
  const [containerLeft, setContainerLeft] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);

  useLayoutEffect(() => {
    if (!containerEl) return;
    const update = () => {
      const rect = containerEl.getBoundingClientRect();
      setContainerLeft(rect.left);
      setContainerWidth(rect.width);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(containerEl);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [containerEl]);

  // Both sentinels are measured together, from their live rects: the bar is
  // "fixed" exactly while the charts straddle the viewport — the top sentinel
  // at or above the fold, the bottom one still below it.
  //
  // The IntersectionObservers can't answer that on their own. They notify on
  // threshold crossings, so a single jump that carries a sentinel clean past
  // the viewport in one frame (scrollIntoView, End, an in-page anchor) takes
  // `isIntersecting` from false to false and fires nothing at all, stranding
  // the bar in whatever mode it was in. They stay on as the cheap trigger for
  // content that grows or shrinks without a scroll; scroll and resize cover
  // the jumps, and the container's ResizeObserver covers charts still sizing.
  useEffect(() => {
    if (scoped || !topSentinelEl || !bottomSentinelEl) return;
    const measure = () => {
      const fold = window.innerHeight;
      setZoomMode(
        topSentinelEl.getBoundingClientRect().top < fold &&
          bottomSentinelEl.getBoundingClientRect().top >= fold
          ? "fixed"
          : "natural",
      );
    };
    measure();
    const io = new IntersectionObserver(measure);
    io.observe(topSentinelEl);
    io.observe(bottomSentinelEl);
    const ro = new ResizeObserver(measure);
    if (containerEl) ro.observe(containerEl);
    window.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure);
    return () => {
      io.disconnect();
      ro.disconnect();
      window.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
    };
  }, [scoped, topSentinelEl, bottomSentinelEl, containerEl]);

  useEffect(() => {
    if (scoped || zoomMode !== "fixed") return;
    document.body.style.setProperty(
      "--chat-bubble-offset",
      `${ZOOM_BAR_HEIGHT}px`,
    );
    return () => {
      document.body.style.removeProperty("--chat-bubble-offset");
    };
  }, [scoped, zoomMode]);

  return {
    setContainerEl,
    setTopSentinelEl,
    setBottomSentinelEl,
    zoomMode,
    containerLeft,
    containerWidth,
  };
}

// The window the charts show, as dates — the PDF's stand-in for the zoom bar.
// Same percentages-of-the-domain arithmetic as toPct, run the other way.
function printedRange(
  timeRange: TimeRange,
  zoom: DataZoomRange | null,
): string | null {
  if (!timeRange) return null;
  const minMs = new Date(timeRange.min).getTime();
  const span = new Date(timeRange.max).getTime() - minMs;
  const at = (pct: number) => formatChartDate(minMs + (span * pct) / 100);
  return `${at(zoom?.start ?? 0)} – ${at(zoom?.end ?? 100)}`;
}

// Oldest event the charts will show, as epoch ms: `range` months back from now,
// mirroring what the backend does with the report's plot_range_months so the
// events bar and the measurements cover the same period. Null means "no cutoff"
// — for "all" (so "Load all data" widens the events too, not just the report)
// and for anything that doesn't yield a real date, since a NaN cutoff would
// silently reject every event rather than none.
function eventWindowStartMs(range: HeartcoreRange): number | null {
  if (typeof range !== "number") return null;
  const start = getServerNow();
  start.setMonth(start.getMonth() - range);
  const ms = start.getTime();
  return Number.isFinite(ms) ? ms : null;
}

// When to plot an event: `event_date`, the day it actually happened — NOT
// `event_timestamp`, which is only when someone typed it in (the events dialog
// stamps that with the submit time), and would put a hospital admission on the
// day of the data entry however long after the fact.
//
// Falls back to the timestamp if the date is missing or unreadable. The field is
// non-null per the schema, but a marker in roughly the wrong place is a far
// better failure than an event that vanishes from the chart with no trace.
function eventDate(e: DefaultPatientEventResponse): string {
  const happened = calendarDateToLocalNoon(e.event_date ?? "");
  return Number.isNaN(new Date(happened).getTime())
    ? e.event_timestamp
    : happened;
}

// Patient event → chart-facing shape; colour + marker come from the backend.
function toChartEvent(e: DefaultPatientEventResponse): ChartEvent {
  const parts: string[] = [];
  if (e.event_category_type)
    parts.push(resolveEnumLabel(eventCategoryType[e.event_category_type]));
  if (e.event_category_details)
    parts.push(
      resolveEnumLabel(eventCategoryDetails[e.event_category_details]),
    );
  return {
    date: eventDate(e),
    category: resolveEnumLabel(eventCategory[e.event_category]),
    subLine: parts.join(" | "),
    description: e.event_description || undefined,
    colorHex: e.event_color_hex,
    shape: e.event_shape as EventShape,
  };
}
